import { uploadBlob } from "../../../helpers/file-upload";
import { makeid_alpa_numeric } from "../../../helpers/utils";
import { TARS_SOURCES_BUCKET } from "../notebook-details/sidebar/add-source/addSourceUtils";
import {
  TARS_CHART_ID_PREFIX,
  TARS_CHART_ID_RANDOM_LENGTH,
  TARS_CHART_IMAGE_CONTENT_TYPE,
  TARS_CHART_IMAGE_FILENAME_EXT,
  TARS_CHART_IMAGE_SOURCE,
  TARS_CHART_PNG_EXPORT_MIN_HEIGHT,
  TARS_CHART_PNG_EXPORT_MIN_WIDTH,
  TARS_CHART_PUBLISH_UPLOAD_FAILED,
  TARS_CHART_S3_CHARTS_SEGMENT,
  TARS_FACETS_S3_PREFIX,
  TARS_S3_PUBLIC_REGION,
} from "../tars-constants";

export function generateChartId(): string {
  return `${TARS_CHART_ID_PREFIX}${makeid_alpa_numeric(TARS_CHART_ID_RANDOM_LENGTH)}`;
}

export function ensureChartEntryId(entry: Record<string, unknown>): string {
  const existing = entry.id;
  if (typeof existing === "string" && existing.trim() !== "") return existing.trim();
  const id = generateChartId();
  entry.id = id;
  return id;
}

/** True when chart entry has a cloud image key from a successful push. */
export function isChartPushedToCloud(entry: Record<string, unknown> | null | undefined): boolean {
  if (entry == null) return false;
  const image = entry.image;
  if (image == null || typeof image !== "object" || Array.isArray(image)) return false;
  const o = image as Record<string, unknown>;
  const s3Key = o.s3_key;
  const url = o.url;
  return (
    (typeof s3Key === "string" && s3Key.trim() !== "") ||
    (typeof url === "string" && url.trim() !== "")
  );
}

export type ChartImageMetadata = {
  s3_bucket: string;
  s3_key: string;
  url: string;
  content_type: string;
  width: number;
  height: number;
  generated_at: string;
  source: string;
};

export function chartImageS3Dir(notebookId: number): string {
  return `${TARS_FACETS_S3_PREFIX}/${notebookId}/${TARS_CHART_S3_CHARTS_SEGMENT}`;
}

function publicS3ObjectUrl(bucket: string, s3Key: string): string {
  return `https://${bucket}.s3-${TARS_S3_PUBLIC_REGION}.amazonaws.com/${s3Key}`;
}

type S3UploadResult = {
  bucket?: string;
  key?: string;
  location?: string;
  status?: number;
};

/** Upload PNG and return image metadata for `more.charts[i].image`. */
export async function uploadChartPngToS3(opts: {
  notebookId: number;
  chartId: string;
  blob: Blob;
}): Promise<ChartImageMetadata> {
  const dir = chartImageS3Dir(opts.notebookId);
  const filename = `${opts.chartId}${TARS_CHART_IMAGE_FILENAME_EXT}`;
  const s3Key = `${dir}/${filename}`;
  const file = new File([opts.blob], filename, {
    type: opts.blob.type || TARS_CHART_IMAGE_CONTENT_TYPE,
  });
  const uploaded = (await uploadBlob(
    dir,
    filename,
    file,
    TARS_SOURCES_BUCKET
  )) as S3UploadResult | null;
  if (!uploaded) throw new Error(TARS_CHART_PUBLISH_UPLOAD_FAILED);

  const resolvedKey =
    typeof uploaded.key === "string" && uploaded.key.trim() !== "" ? uploaded.key : s3Key;
  const url =
    typeof uploaded.location === "string" && uploaded.location.trim() !== ""
      ? uploaded.location
      : publicS3ObjectUrl(TARS_SOURCES_BUCKET, resolvedKey);

  return {
    s3_bucket: TARS_SOURCES_BUCKET,
    s3_key: resolvedKey,
    url,
    content_type: TARS_CHART_IMAGE_CONTENT_TYPE,
    width: TARS_CHART_PNG_EXPORT_MIN_WIDTH,
    height: TARS_CHART_PNG_EXPORT_MIN_HEIGHT,
    generated_at: new Date().toISOString(),
    source: TARS_CHART_IMAGE_SOURCE,
  };
}

/** Patch one chart entry in `more.charts` with id + image metadata. */
export function applyChartImageToMore(opts: {
  more: Record<string, unknown>;
  chartIndex: number;
  chartEntry: Record<string, unknown>;
  image: ChartImageMetadata;
}): Record<string, unknown> {
  const chartsRaw = opts.more.charts;
  const charts = Array.isArray(chartsRaw)
    ? chartsRaw.map((c) =>
        c != null && typeof c === "object" && !Array.isArray(c)
          ? { ...(c as Record<string, unknown>) }
          : c
      )
    : [];
  if (opts.chartIndex < 0 || opts.chartIndex >= charts.length) {
    throw new Error("Chart index is out of date");
  }
  const nextEntry = { ...opts.chartEntry, image: opts.image };
  ensureChartEntryId(nextEntry);
  charts[opts.chartIndex] = nextEntry;
  return { ...opts.more, charts };
}
